import React, { useEffect, useLayoutEffect, useState, useCallback, useMemo, useRef } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd';
import { format, parseISO } from 'date-fns';
import { fetchTasks, createTask, updateTask, deleteTask, reorderTasks, clearTasks } from '../../store/slices/tasksSlice';

// Date utility functions - handles timezone-safe date operations
// These prevent the common issue where dates shift by one day due to timezone conversions

/**
 * Convert a YYYY-MM-DD date string to an ISO string for API submission.
 * Uses noon local time to prevent date shift when converted to UTC.
 * @param {string} dateStr - Date string in YYYY-MM-DD format from HTML date input
 * @returns {string|null} - ISO string safe for API submission, or null if no date
 */
const dateToISOString = (dateStr) => {
  if (!dateStr) return null;
  // Append T12:00:00 (noon) to prevent date shift when converting to UTC
  // Using noon ensures the date stays the same in any timezone up to UTC±12
  const dateAtNoon = new Date(`${dateStr}T12:00:00`);
  return dateAtNoon.toISOString();
};

/**
 * Extract YYYY-MM-DD string from an ISO date string for HTML date input.
 * Properly handles timezone by parsing the date and formatting in local time.
 * @param {string} isoStr - ISO date string from API
 * @returns {string} - Date string in YYYY-MM-DD format for HTML date input
 */
const isoToDateInput = (isoStr) => {
  if (!isoStr) return '';
  // Parse the ISO string and format as local date in YYYY-MM-DD
  const date = parseISO(isoStr);
  return format(date, 'yyyy-MM-dd');
};
import {
  fetchSprints, createSprint, updateSprint,
  startSprint, completeSprint, deleteSprint,
  assignTasksToSprint, removeTasksFromSprint
} from '../../store/slices/sprintsSlice';
import Modal from '../../components/common/Modal';
import ConfirmModal from '../../components/common/ConfirmModal';
import CloneTaskModal from '../../components/common/CloneTaskModal';
import RichTextEditor from '../../components/common/RichTextEditor';
import ParentSelector from '../../components/common/ParentSelector';
import TicketTypeSelector, { TicketTypeIcon, InlineTypeSelector, TICKET_TYPES } from '../../components/common/TicketTypeSelector';
import SearchableSelect from '../../components/common/SearchableSelect';
import { Avatar } from '../../components/common/Avatar';
import EpicPanel from '../../components/common/EpicPanel';
import { useTaskTypes } from '../../hooks/useTaskTypes';
import api from '../../services/api';
import { validationMessages } from '../../utils/validationMessages';
import './ProjectBacklog.css';

// Priority configuration
const PRIORITIES = {
  highest: { color: '#ff5630', label: 'Highest' },
  high: { color: '#ff7452', label: 'High' },
  medium: { color: '#ffab00', label: 'Medium' },
  low: { color: '#2684ff', label: 'Low' },
  lowest: { color: '#57d9a3', label: 'Lowest' },
};

// Map backend field keys to human-readable labels for mandatory-field errors
const COMPLETION_FIELD_LABELS = {
  assignee: 'Assignee',
  actual_hours: 'Actual Hours',
  estimated_hours_or_story_points: 'Estimated Hours or Story Points',
};

const formatMandatoryFieldsError = (error) => {
  if (error && typeof error === 'object' && error.error === 'mandatory_fields_missing') {
    const names = (error.fields || []).map(f => COMPLETION_FIELD_LABELS[f] || f).join(', ');
    return `Cannot close ticket. Please fill required fields: ${names}`;
  }
  return null;
};

const ProjectBacklog = ({ projectId, members, canManage, onOpenTask }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useSelector((state) => state.auth);
  const { tasks, loading: tasksLoading } = useSelector((state) => state.tasks);
  const { sprints, activeSprint } = useSelector((state) => state.sprints);

  // Load task types from API
  const { taskTypes, getTypeByKey } = useTaskTypes(projectId);

  // Workflow statuses for status dropdown
  const [workflowStatuses, setWorkflowStatuses] = useState([
    { key: 'backlog', name: 'Backlog' },
    { key: 'in_progress', name: 'In Progress' },
    { key: 'review', name: 'Review' },
    { key: 'done', name: 'Done' },
  ]);

  // Filter state
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedAssignees, setSelectedAssignees] = useState([]);
  const [selectedType, setSelectedType] = useState('');
  const [selectedSprint, setSelectedSprint] = useState('');
  const [showAssigneeDropdown, setShowAssigneeDropdown] = useState(false);
  
  // Epic panel state (domain rule: epics must not appear in lists, only in panel)
  const [showEpicPanel, setShowEpicPanel] = useState(false);

  // Section expand/collapse state
  const [expandedSprints, setExpandedSprints] = useState({});
  const [backlogExpanded, setBacklogExpanded] = useState(true);

  // Expand sprints by default — only for sprints the user hasn't explicitly toggled.
  // Using a functional update means we never overwrite user-set collapsed/expanded choices
  // even when the sprints array reference changes (e.g. after a ProjectPage re-fetch).
  useEffect(() => {
    if (sprints && sprints.length > 0) {
      setExpandedSprints(prev => {
        const updates = {};
        sprints.forEach(sprint => {
          if (prev[sprint.id] === undefined) {
            updates[sprint.id] = true;
          }
        });
        return Object.keys(updates).length > 0 ? { ...prev, ...updates } : prev;
      });
    }
  }, [sprints]);

  // Hierarchy expand/collapse state
  const [expandedItems, setExpandedItems] = useState({});

  // Create task modal
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [targetSprintForCreate, setTargetSprintForCreate] = useState(null); // Track which sprint to create ticket for
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    task_type: '',  // No default - user must select
    priority: 'medium',
    assignee_id: '',
    story_points: '',
    parent_id: null,
  });
  const [creating, setCreating] = useState(false);

  // Sprint modals
  const [showSprintModal, setShowSprintModal] = useState(false);
  const [editingSprint, setEditingSprint] = useState(null);
  const [sprintFormData, setSprintFormData] = useState({
    name: '',
    goal: '',
    start_date: '',
    end_date: '',
  });
  const [showCompleteModal, setShowCompleteModal] = useState(false);
  const [completeData, setCompleteData] = useState({
    move_incomplete_to_backlog: true,
    move_incomplete_to_sprint_id: '',
  });

  // Quick create state
  const [quickCreateTarget, setQuickCreateTarget] = useState(null); // 'backlog' or sprint id
  const [quickCreateTitle, setQuickCreateTitle] = useState('');
  const [quickCreateType, setQuickCreateType] = useState('story');
  const quickCreateRef = useRef(null);

  // Inline editing state
  const [inlineEdit, setInlineEdit] = useState({ taskId: null, field: null });
  const [inlineEditValue, setInlineEditValue] = useState('');
  const [inlineEditSaving, setInlineEditSaving] = useState(false);

  // More actions menu state
  const [activeMenuTaskId, setActiveMenuTaskId] = useState(null);
  const [activeSubmenu, setActiveSubmenu] = useState(null);
  const [menuPosition, setMenuPosition] = useState({ top: 0, left: 0 });
  const moreActionsRef = useRef(null);

  const [cloneModalTask, setCloneModalTask] = useState(null);

  const [contextMenu, setContextMenu] = useState({ visible: false, x: 0, y: 0, task: null });
  const contextMenuRef = useRef(null);

  useEffect(() => {
    if (!contextMenu.visible) return;
    const close = (e) => {
      if (contextMenuRef.current && !contextMenuRef.current.contains(e.target)) {
        setContextMenu(m => ({ ...m, visible: false }));
      }
    };
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, [contextMenu.visible]);

  const handleContextMenu = (e, task) => {
    e.preventDefault();
    setContextMenu({ visible: true, x: e.clientX, y: e.clientY, task });
  };

  // Confirm modal state
  const [confirmModal, setConfirmModal] = useState({
    isOpen: false,
    title: '',
    message: '',
    confirmText: 'Confirm',
    variant: 'danger',
    onConfirm: null,
    isLoading: false
  });

  // Load data
  useLayoutEffect(() => {
    dispatch(clearTasks());
    dispatch(fetchTasks({ projectId, pageSize: 5000 }));
    dispatch(fetchSprints({ projectId }));
  }, [dispatch, projectId]);

  // Auto-expand active sprint
  useEffect(() => {
    if (activeSprint) {
      setExpandedSprints(prev => ({ ...prev, [activeSprint.id]: true }));
    }
  }, [activeSprint]);

  // Close dropdowns on outside click
  useEffect(() => {
    const handleClickOutside = (e) => {
      // If the click originated in an element that intentionally ignores modal/overlay-close
      const clickedIgnore = e.target && e.target.closest && e.target.closest('[data-ignore-modal-close]');

      if (showAssigneeDropdown && !e.target.closest('.assignee-filter') && !clickedIgnore) {
        setShowAssigneeDropdown(false);
      }

      if (activeMenuTaskId !== null && !e.target.closest('.more-actions-menu') && !e.target.closest('.action-btn') && !clickedIgnore) {
        setActiveMenuTaskId(null);
        setActiveSubmenu(null);
      }

      if (quickCreateTarget !== null && quickCreateRef.current && !quickCreateRef.current.contains(e.target) && !clickedIgnore) {
        setQuickCreateTarget(null);
        setQuickCreateTitle('');
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showAssigneeDropdown, quickCreateTarget, activeMenuTaskId]);

  // Fetch workflow statuses for this project
  const fetchWorkflowStatuses = useCallback(async () => {
    try {
      const response = await api.get(`/workflows/projects/${projectId}/statuses`);
      if (response.data.items && response.data.items.length > 0) {
        setWorkflowStatuses(response.data.items);
      }
    } catch (error) {
      console.error('Failed to fetch workflow statuses:', error);
      // Keep default statuses on error
    }
  }, [projectId]);

  // Load workflow statuses on mount
  useEffect(() => {
    if (projectId) {
      fetchWorkflowStatuses();
    }
  }, [projectId, fetchWorkflowStatuses]);

  // Get unique epics from tasks
  const epics = useMemo(() => {
    return tasks.filter(t => t.task_type === 'epic');
  }, [tasks]);

  // Build a complete task map for cross-referencing parent info across sprints
  const allTasksMap = useMemo(() => {
    const map = new Map();
    tasks.forEach(task => {
      // Find which sprint this task belongs to
      const taskSprint = sprints.find(s => s.id === task.sprint_id);
      map.set(task.id, { ...task, sprintInfo: taskSprint || null });
    });
    return map;
  }, [tasks, sprints]);

  // Organize tasks into hierarchy and by sprint
  const organizedData = useMemo(() => {
    // Get terminal status keys (Done, Closed, etc.)
    const terminalStatusKeys = new Set(
      workflowStatuses.filter(ws => ws.is_terminal).map(ws => ws.key)
    );
    
    // Backlog: tasks without sprint AND not in terminal status AND not epics
    // Domain rule: Epics must NEVER appear in backlog list, only in epic panel
    const backlogItems = tasks.filter(t => 
      !t.sprint_id && !terminalStatusKeys.has(t.status) && t.task_type !== 'epic'
    );
    
    const sprintTasks = {};
    sprints.forEach(sprint => {
      // Domain rule: Epics must NEVER appear in sprint lists
      sprintTasks[sprint.id] = tasks.filter(t => t.sprint_id === sprint.id && t.task_type !== 'epic');
    });

    const buildHierarchy = (taskList, includeOrphanedChildren = false) => {
      const taskMap = new Map(taskList.map(t => [t.id, { ...t, children: [] }]));
      const roots = [];

      taskList.forEach(task => {
        const taskWithChildren = taskMap.get(task.id);
        
        // Check if parent exists in the same section
        if (task.parent_id && taskMap.has(task.parent_id)) {
          taskMap.get(task.parent_id).children.push(taskWithChildren);
        } else if (task.parent_id && includeOrphanedChildren) {
          // Parent is in a different section (e.g., sprint) - mark as orphaned with parent context
          const parentTask = allTasksMap.get(task.parent_id);
          if (parentTask) {
            taskWithChildren.parentContext = {
              id: parentTask.id,
              task_key: parentTask.task_key,
              title: parentTask.title,
              task_type: parentTask.task_type,
              sprintInfo: parentTask.sprintInfo,
            };
          }
          roots.push(taskWithChildren);
        } else {
          roots.push(taskWithChildren);
        }
      });

      const sortTasks = (a, b) => {
        return (a.position || 0) - (b.position || 0);
      };

      roots.sort(sortTasks);
      roots.forEach(r => {
        r.children.sort(sortTasks);
        r.children.forEach(c => c.children?.sort(sortTasks));
      });

      return roots;
    };

    // Order sprints: active first, then planned, then completed
    const orderedSprints = [...sprints].sort((a, b) => {
      const statusOrder = { active: 0, planned: 1, completed: 2 };
      return (statusOrder[a.status] ?? 3) - (statusOrder[b.status] ?? 3);
    });

    // Count children in backlog whose parents are in sprints
    const orphanedChildrenCount = backlogItems.filter(t => 
      t.parent_id && !backlogItems.some(b => b.id === t.parent_id)
    ).length;

    const backlogHierarchy = buildHierarchy(backlogItems, true);
    return {
      backlog: backlogHierarchy,
      backlogCount: backlogHierarchy.length,
      orphanedChildrenCount,
      sprints: orderedSprints.map(sprint => ({
        ...sprint,
        tasks: buildHierarchy(sprintTasks[sprint.id] || [], false),
        taskCount: (sprintTasks[sprint.id] || []).length,
        // Count children in backlog for tasks in this sprint
        backlogChildrenCount: backlogItems.filter(t => 
          sprintTasks[sprint.id]?.some(st => st.id === t.parent_id)
        ).length,
      })),
    };
  }, [tasks, sprints, allTasksMap, workflowStatuses]);

  // Filter tasks
  const filterTasks = useCallback((taskList) => {
    const filterRecursive = (items) => {
      return items.reduce((acc, task) => {
        // Check if task matches filters
        let matches = true;

        if (searchQuery) {
          const query = searchQuery.toLowerCase();
          const matchesKey = task.task_key?.toLowerCase().includes(query);
          const matchesTitle = task.title?.toLowerCase().includes(query);
          matches = matches && (matchesKey || matchesTitle);
        }

        if (selectedType) {
          matches = matches && task.task_type === selectedType;
        }

        if (selectedAssignees.length > 0) {
          const includeUnassigned = selectedAssignees.includes('unassigned');
          const assignedIds = selectedAssignees.filter(id => id !== 'unassigned');
          
          if (includeUnassigned && assignedIds.length === 0) {
            // Only show unassigned
            matches = matches && !task.assignee;
          } else if (includeUnassigned) {
            // Show both unassigned and assigned to selected users
            matches = matches && (!task.assignee || assignedIds.includes(task.assignee.id));
          } else {
            // Only show assigned to selected users
            matches = matches && task.assignee && assignedIds.includes(task.assignee.id);
          }
        }

        // Filter children recursively
        const filteredChildren = task.children ? filterRecursive(task.children) : [];

        // Include task if it matches or has matching children
        if (matches || filteredChildren.length > 0) {
          acc.push({
            ...task,
            children: filteredChildren,
          });
        }

        return acc;
      }, []);
    };

    return filterRecursive(taskList);
  }, [searchQuery, selectedType, selectedAssignees]);

  // Helper to count all tasks recursively (including children)
  const countTasksRecursive = useCallback((taskList) => {
    return taskList.reduce((count, task) => {
      return count + 1 + (task.children ? countTasksRecursive(task.children) : 0);
    }, 0);
  }, []);

  // Apply filters to organized data
  const filteredData = useMemo(() => {
    const hasFilters = searchQuery || selectedType || selectedAssignees.length > 0 || selectedSprint;
    if (!hasFilters) return organizedData;

    const filteredBacklog = (selectedSprint && selectedSprint !== 'backlog')
      ? []
      : filterTasks(organizedData.backlog);

    const filteredSprints = selectedSprint === 'backlog'
      ? []
      : organizedData.sprints
          .filter(sprint => !selectedSprint || String(sprint.id) === selectedSprint)
          .map(sprint => {
            const filteredSprintTasks = filterTasks(sprint.tasks);
            return {
              ...sprint,
              tasks: filteredSprintTasks,
              taskCount: countTasksRecursive(filteredSprintTasks),
            };
          });

    return {
      backlog: filteredBacklog,
      backlogCount: (selectedSprint && selectedSprint !== 'backlog') ? 0 : filteredBacklog.length,
      sprints: filteredSprints,
    };
  }, [organizedData, filterTasks, searchQuery, selectedType, selectedAssignees, selectedSprint, countTasksRecursive]);

  // Expand/collapse handlers
  const toggleItemExpanded = (taskId, e) => {
    e.preventDefault();
    e.stopPropagation();
    setExpandedItems(prev => ({ ...prev, [taskId]: prev[taskId] === false ? true : false }));
  };

  const hasExpandableChildren = (task) => {
    return (task.task_type === 'epic' || task.task_type === 'story' || task.task_type === 'task') &&
      task.children && task.children.length > 0;
  };

  const isItemExpanded = (taskId) => {
    return expandedItems[taskId] === true;
  };

  const toggleSprintExpanded = (sprintId) => {
    setExpandedSprints(prev => ({ ...prev, [sprintId]: !prev[sprintId] }));
  };

  // Drag and drop handler
  const handleDragEnd = useCallback((result) => {
    const { destination, source, draggableId } = result;
    if (!destination) return;
    if (destination.droppableId === source.droppableId && destination.index === source.index) return;

    const taskId = parseInt(draggableId.replace('task-', ''));
    const sourceDropId = source.droppableId;
    const destDropId = destination.droppableId;
    const sourceSprintId = sourceDropId === 'backlog' ? null : parseInt(sourceDropId.match(/\d+/)?.[0]);
    const destSprintId = destDropId === 'backlog' ? null : parseInt(destDropId.match(/\d+/)?.[0]);

    const sourceSprint = sprints.find(s => s.id === sourceSprintId);
    const destSprint = sprints.find(s => s.id === destSprintId);

    if (destSprint && destSprint.status === 'completed') {
      toast.error(`Cannot move tasks to completed sprint "${destSprint.name}"`);
      return;
    }
    if (sourceSprint && sourceSprint.status === 'completed') {
      toast.error(`Cannot move tasks from completed sprint "${sourceSprint.name}". Tickets in a closed sprint are locked.`);
      return;
    }

    if (sourceDropId === destDropId) {
      const sectionTasks = sourceSprintId === null
        ? [...filteredData.backlog]
        : [...(filteredData.sprints.find(s => s.id === sourceSprintId)?.tasks || [])];

      const [moved] = sectionTasks.splice(source.index, 1);
      sectionTasks.splice(destination.index, 0, moved);

      const updates = sectionTasks.map((t, idx) => ({ id: t.id, position: (idx + 1) * 1000 }));

      dispatch(reorderTasks(updates));

      api.put(`/projects/${projectId}/tasks/reorder`, { updates }).catch(() => {
        toast.error('Failed to save order, please refresh');
        dispatch(fetchTasks({ projectId, pageSize: 5000 }));
      });

      return;
    }

    const draggedTask = tasks.find(t => t.id === taskId);
    const taskTypeLabel = draggedTask?.task_type
      ? draggedTask.task_type.charAt(0).toUpperCase() + draggedTask.task_type.slice(1)
      : 'Issue';

    dispatch(updateTask({ taskId, taskData: { sprint_id: destSprintId } }));

    if (destSprintId === null) {
      toast.success(`${taskTypeLabel} moved to backlog`);
      dispatch(removeTasksFromSprint({ projectId, taskIds: [taskId] }));
    } else {
      toast.success(`${taskTypeLabel} moved to ${destSprint?.name || 'sprint'}`);
      dispatch(assignTasksToSprint({ projectId, sprintId: destSprintId, taskIds: [taskId] }));
    }
  }, [dispatch, projectId, tasks, sprints, filteredData]);

  // Quick create handler
  const handleQuickCreate = async (e) => {
    e.preventDefault();
    if (!quickCreateTitle.trim()) return;

    setCreating(true);
    try {
      const sprintId = quickCreateTarget === 'backlog' ? null : quickCreateTarget;
      const taskData = {
        title: quickCreateTitle.trim(),
        task_type: quickCreateType,
        priority: 'medium',
        sprint_id: sprintId,
      };
      await dispatch(createTask({ projectId, taskData })).unwrap();
      const typeLabel = quickCreateType.charAt(0).toUpperCase() + quickCreateType.slice(1);
      const sprintLabel = quickCreateTarget && quickCreateTarget !== 'backlog' ? ` in ${sprints.find(s => s.id === quickCreateTarget)?.name || 'sprint'}` : ' in backlog';
      toast.success(`${typeLabel} created${sprintLabel}`);
      setQuickCreateTitle('');
      setQuickCreateType('story');
      setQuickCreateTarget(null);
      dispatch(fetchTasks({ projectId, pageSize: 5000 }));
    } catch (error) {
      toast.error(error || 'Failed to create issue');
    } finally {
      setCreating(false);
    }
  };

  // Epic click handler (domain rule: epics open in panel, not in list)
  const handleEpicClick = (epic, e) => {
    e?.stopPropagation();
    e?.preventDefault();
    if (e && (e.ctrlKey || e.metaKey || e.shiftKey || e.button === 1)) {
      window.open(`/projects/${projectId}/board/${epic.task_key}`);
    } else if (onOpenTask) {
      onOpenTask(epic.task_key);
    } else {
      navigate(`/projects/${projectId}/board/${epic.task_key}`, {
        state: {
          viewMode: 'modal',
          returnTo: location.pathname
        }
      });
    }
  };



  // Full create handler
  const handleCreateTask = async (e) => {
    e.preventDefault();
    if (!formData.task_type) {
      toast.error(validationMessages.task.type.required);
      return;
    }
    if (!formData.title.trim()) {
      toast.error(validationMessages.task.title.required);
      return;
    }

    setCreating(true);
    try {
      const taskData = {
        ...formData,
        sprint_id: targetSprintForCreate,
        assignee_id: formData.assignee_id ? parseInt(formData.assignee_id) : null,
        story_points: formData.story_points ? parseInt(formData.story_points) : null,
      };
      await dispatch(createTask({ projectId, taskData })).unwrap();
      const typeLabel = formData.task_type.charAt(0).toUpperCase() + formData.task_type.slice(1);
      const sprintLabel = targetSprintForCreate ? ` in ${sprints.find(s => s.id === targetSprintForCreate)?.name || 'sprint'}` : ' in backlog';
      toast.success(`${typeLabel} created${sprintLabel}`);
      setShowCreateModal(false);
      setTargetSprintForCreate(null);
      resetForm();
      dispatch(fetchTasks({ projectId, pageSize: 5000 }));
    } catch (error) {
      toast.error(error || 'Failed to create issue');
    } finally {
      setCreating(false);
    }
  };

  const resetForm = () => {
    setFormData({
      title: '',
      description: '',
      task_type: '',  // No default - user must select
      priority: 'medium',
      assignee_id: '',
      story_points: '',
      parent_id: null,
    });
  };

  // Inline editing handlers
  const startInlineEdit = (taskId, field, currentValue) => {
    if (!canManage) return;
    setInlineEdit({ taskId, field });
    setInlineEditValue(currentValue !== null && currentValue !== undefined ? String(currentValue) : '');
  };

  const cancelInlineEdit = () => {
    setInlineEdit({ taskId: null, field: null });
    setInlineEditValue('');
  };

  const saveInlineEdit = async (taskId, field, value) => {
    if (inlineEditSaving) return;

    setInlineEditSaving(true);
    try {
      let updateData = {};

      switch (field) {
        case 'priority':
        case 'status':
          updateData[field] = value;
          break;
        case 'assignee_id':
          updateData.assignee_id = value ? parseInt(value) : null;
          break;
        case 'sprint_id': {
          // Prevent changing sprint for tasks in a completed sprint
          const inlineTask = tasks.find(t => t.id === taskId);
          if (inlineTask?.sprint_id) {
            const currentSprint = sprints.find(s => s.id === inlineTask.sprint_id);
            if (currentSprint && currentSprint.status === 'completed') {
              toast.error(`Cannot move tasks from completed sprint "${currentSprint.name}". Tickets in a closed sprint are locked.`);
              setInlineEditSaving(false);
              return;
            }
          }
          updateData.sprint_id = value ? parseInt(value) : null;
          break;
        }
        case 'story_points':
          updateData.story_points = value ? parseInt(value) : null;
          break;
        case 'parent_id':
          // -1 to remove parent, otherwise set the new parent
          updateData.parent_id = value ? parseInt(value) : -1;
          break;
        default:
          break;
      }

      await dispatch(updateTask({ taskId, taskData: updateData })).unwrap();
      toast.success('Updated');
      dispatch(fetchTasks({ projectId, pageSize: 5000 }));
      cancelInlineEdit();
    } catch (error) {
      const mandatoryMsg = formatMandatoryFieldsError(error);
      toast.error(mandatoryMsg || (typeof error === 'string' ? error : 'Failed to update'));
    } finally {
      setInlineEditSaving(false);
    }
  };

  const handleInlineSelectChange = (e, taskId, field) => {
    e.stopPropagation();
    saveInlineEdit(taskId, field, e.target.value);
  };

  const getTaskUrl = (task) => {
    const parentKey = task.parent?.task_key || task.parentContext?.task_key;
    if (parentKey) {
      return `/projects/${projectId}/board/${parentKey}/${task.task_key}`;
    }
    return `/projects/${projectId}/board/${task.task_key}`;
  };

  // More actions handlers
  const toggleMoreActionsMenu = (e, taskId) => {
    e.stopPropagation();
    if (activeMenuTaskId === taskId) {
      setActiveMenuTaskId(null);
      setActiveSubmenu(null);
    } else {
      // Calculate position based on button location
      const button = e.currentTarget;
      const rect = button.getBoundingClientRect();
      const menuWidth = 220;
      const menuHeight = 400; // approximate max height

      let top = rect.bottom + 4;
      let left = rect.right - menuWidth;

      // Adjust if menu would go off screen
      if (left < 10) left = 10;
      if (top + menuHeight > window.innerHeight) {
        top = rect.top - menuHeight - 4;
        if (top < 10) top = 10;
      }

      setMenuPosition({ top, left });
      setActiveMenuTaskId(taskId);
      setActiveSubmenu(null);
    }
  };

  const closeMoreActionsMenu = () => {
    setActiveMenuTaskId(null);
    setActiveSubmenu(null);
  };

  const handleCopyLink = (task) => {
    const url = `${window.location.origin}${getTaskUrl(task)}`;
    navigator.clipboard.writeText(url);
    toast.success('Link copied to clipboard');
    closeMoreActionsMenu();
  };

  const handleCopyKey = (task) => {
    navigator.clipboard.writeText(task.task_key);
    toast.success('Key copied to clipboard');
    closeMoreActionsMenu();
  };

  const handleToggleFlag = async (task) => {
    try {
      await dispatch(updateTask({
        taskId: task.id,
        taskData: { is_flagged: !task.is_flagged }
      })).unwrap();
      toast.success(task.is_flagged ? 'Flag removed' : 'Flag added');
      dispatch(fetchTasks({ projectId, pageSize: 5000 }));
    } catch (error) {
      toast.error('Failed to update flag');
    }
    closeMoreActionsMenu();
  };

  const handleMoveToTop = async (task) => {
    const targetTasks = task.sprint_id
      ? tasks.filter(t => t.sprint_id === task.sprint_id && t.task_type !== 'epic')
      : tasks.filter(t => !t.sprint_id && t.task_type !== 'epic');

    const positions = targetTasks.map(t => t.position || 0);
    const minPosition = Math.min(...positions);

    if (task.position === minPosition) {
      closeMoreActionsMenu();
      return;
    }

    try {
      await dispatch(updateTask({
        taskId: task.id,
        taskData: { position: minPosition - 1 }
      })).unwrap();
      toast.success('Moved to top');
      dispatch(fetchTasks({ projectId, pageSize: 5000 }));
    } catch (error) {
      toast.error('Failed to move task');
    }
    closeMoreActionsMenu();
  };

  const handleMoveToBottom = async (task) => {
    const targetTasks = task.sprint_id
      ? tasks.filter(t => t.sprint_id === task.sprint_id && t.task_type !== 'epic')
      : tasks.filter(t => !t.sprint_id && t.task_type !== 'epic');

    const positions = targetTasks.map(t => t.position || 0);
    const maxPosition = Math.max(...positions);

    if (task.position === maxPosition) {
      closeMoreActionsMenu();
      return;
    }

    try {
      await dispatch(updateTask({
        taskId: task.id,
        taskData: { position: maxPosition + 1 }
      })).unwrap();
      toast.success('Moved to bottom');
      dispatch(fetchTasks({ projectId, pageSize: 5000 }));
    } catch (error) {
      toast.error('Failed to move task');
    }
    closeMoreActionsMenu();
  };

  const handleMoveToSprint = async (task, sprintId) => {
    // Prevent moving tasks from a completed sprint
    if (task.sprint_id) {
      const currentSprint = sprints.find(s => s.id === task.sprint_id);
      if (currentSprint && currentSprint.status === 'completed') {
        toast.error(`Cannot move tasks from completed sprint "${currentSprint.name}". Tickets in a closed sprint are locked.`);
        closeMoreActionsMenu();
        return;
      }
    }
    try {
      await dispatch(updateTask({
        taskId: task.id,
        taskData: { sprint_id: sprintId || null }
      })).unwrap();
      toast.success(sprintId ? 'Moved to sprint' : 'Moved to backlog');
      dispatch(fetchTasks({ projectId, pageSize: 5000 }));
    } catch (error) {
      toast.error(error || 'Failed to move task');
    }
    closeMoreActionsMenu();
  };

  const handleQuickAssign = async (task, assigneeId) => {
    try {
      await dispatch(updateTask({
        taskId: task.id,
        taskData: { assignee_id: assigneeId || null }
      })).unwrap();
      toast.success('Assignee updated');
      dispatch(fetchTasks({ projectId, pageSize: 5000 }));
    } catch (error) {
      toast.error('Failed to update assignee');
    }
    closeMoreActionsMenu();
  };

  const handleQuickPriority = async (task, priority) => {
    try {
      await dispatch(updateTask({
        taskId: task.id,
        taskData: { priority }
      })).unwrap();
      toast.success('Priority updated');
      dispatch(fetchTasks({ projectId, pageSize: 5000 }));
    } catch (error) {
      toast.error('Failed to update priority');
    }
    closeMoreActionsMenu();
  };

  const handleQuickParent = async (task, parentId) => {
    // CRITICAL: Subtasks cannot have their parent removed
    if (task.task_type === 'subtask' && parentId === null) {
      toast.error(validationMessages.task.subtask.parentRequired);
      closeMoreActionsMenu();
      return;
    }
    
    // Validate parent type for subtasks (must be story or task)
    if (task.task_type === 'subtask' && parentId !== null) {
      const targetParent = tasks.find(t => t.id === parentId);
      if (targetParent && !['story', 'task'].includes(targetParent.task_type)) {
        toast.error(validationMessages.task.subtask.invalidParent);
        closeMoreActionsMenu();
        return;
      }
    }
    
    try {
      await dispatch(updateTask({
        taskId: task.id,
        taskData: { parent_id: parentId === null ? -1 : parentId }
      })).unwrap();
      toast.success('Parent updated');
      dispatch(fetchTasks({ projectId, pageSize: 5000 }));
    } catch (error) {
      toast.error(error || 'Failed to update parent');
    }
    closeMoreActionsMenu();
  };

  const handleDeleteTask = (task) => {
    closeMoreActionsMenu();
    setConfirmModal({
      isOpen: true,
      title: 'Delete Task',
      message: `Are you sure you want to delete "${task.task_key}"? This action cannot be undone.`,
      confirmText: 'Delete',
      variant: 'danger',
      isLoading: false,
      onConfirm: async () => {
        setConfirmModal(prev => ({ ...prev, isLoading: true }));
        try {
          await dispatch(deleteTask(task.id)).unwrap();
          const taskTypeLabel = task.task_type.charAt(0).toUpperCase() + task.task_type.slice(1);
          toast.success(`${taskTypeLabel} deleted`);
          dispatch(fetchTasks({ projectId, pageSize: 5000 }));
          setConfirmModal(prev => ({ ...prev, isOpen: false, isLoading: false }));
        } catch (error) {
          toast.error('Failed to delete task');
          setConfirmModal(prev => ({ ...prev, isLoading: false }));
        }
      }
    });
  };

  const handleCloneTask = (task) => {
    closeMoreActionsMenu();
    setCloneModalTask(task);
  };

  // Get potential parent tasks for submenu based on hierarchy rules
  const getPotentialParents = useCallback((task) => {
    // Subtasks can only be under story or task (NOT epic)
    if (task.task_type === 'subtask') {
      return tasks.filter(t =>
        t.id !== task.id &&
        ['story', 'task'].includes(t.task_type)
      );
    }
    // Bugs can be under epic, story, or task
    if (task.task_type === 'bug') {
      return tasks.filter(t =>
        t.id !== task.id &&
        ['epic', 'story', 'task'].includes(t.task_type)
      );
    }
    // Stories/Tasks can be under epic
    if (['story', 'task'].includes(task.task_type)) {
      return tasks.filter(t =>
        t.id !== task.id &&
        t.task_type === 'epic'
      );
    }
    // Epic has no parent
    if (task.task_type === 'epic') {
      return [];
    }
    // Default: epic, story as potential parents
    return tasks.filter(t =>
      t.id !== task.id &&
      ['epic', 'story'].includes(t.task_type)
    );
  }, [tasks]);

  // Sprint handlers
  const openCreateSprintModal = () => {
    setEditingSprint(null);
    setSprintFormData({ name: '', goal: '', start_date: '', end_date: '' });
    setShowSprintModal(true);
  };

  const openEditSprintModal = (sprint, e) => {
    e.stopPropagation();
    setEditingSprint(sprint);
    setSprintFormData({
      name: sprint.name,
      goal: sprint.goal || '',
      // Use timezone-safe date extraction to prevent date shift
      start_date: isoToDateInput(sprint.start_date),
      end_date: isoToDateInput(sprint.end_date),
    });
    setShowSprintModal(true);
  };

  const handleSprintSubmit = async (e) => {
    e.preventDefault();

    // Frontend validation with specific error messages
    const errors = [];
    
    if (!sprintFormData.name.trim()) {
      errors.push('Sprint Name is required.');
    }
    
    if (!sprintFormData.start_date) {
      errors.push('Sprint Start Date is required.');
    }
    
    if (!sprintFormData.end_date) {
      errors.push('Sprint End Date is required.');
    }
    
    if (sprintFormData.start_date && sprintFormData.end_date) {
      const startDate = new Date(`${sprintFormData.start_date}T00:00:00`);
      const endDate = new Date(`${sprintFormData.end_date}T00:00:00`);
      
      if (endDate <= startDate) {
        errors.push('Sprint End Date must be after Start Date.');
      }
    }

    // Show first error
    if (errors.length > 0) {
      toast.error(errors[0]);
      return;
    }

    try {
      const data = {
        name: sprintFormData.name,
        goal: sprintFormData.goal || null,
        // Use timezone-safe date conversion to prevent date shift
        start_date: dateToISOString(sprintFormData.start_date),
        end_date: dateToISOString(sprintFormData.end_date),
      };

      if (editingSprint) {
        await dispatch(updateSprint({ projectId, sprintId: editingSprint.id, sprintData: data })).unwrap();
        toast.success('Sprint updated successfully.');
      } else {
        await dispatch(createSprint({ projectId, sprintData: data })).unwrap();
        toast.success('Sprint created successfully.');
      }

      setShowSprintModal(false);
      dispatch(fetchSprints({ projectId }));
    } catch (error) {
      // Handle backend errors with specific messages
      const errorMessage = error?.response?.data?.detail || error || 'Failed to save sprint';
      toast.error(errorMessage);
    }
  };

  const handleStartSprint = (sprint, e) => {
    e.stopPropagation();
    if (activeSprint) {
      toast.error(`Cannot start sprint. "${activeSprint.name}" is already active.`);
      return;
    }
    setConfirmModal({
      isOpen: true,
      title: 'Start Sprint',
      message: `Are you sure you want to start "${sprint.name}"? This will make it the active sprint.`,
      confirmText: 'Start Sprint',
      variant: 'info',
      isLoading: false,
      onConfirm: async () => {
        setConfirmModal(prev => ({ ...prev, isLoading: true }));
        try {
          const result = await dispatch(startSprint({ projectId, sprintId: sprint.id })).unwrap();
          toast.success(`Sprint "${result?.name || sprint.name}" started`);
          dispatch(fetchSprints({ projectId }));
          setConfirmModal(prev => ({ ...prev, isOpen: false, isLoading: false }));
        } catch (error) {
          toast.error(error || 'Failed to start sprint');
          setConfirmModal(prev => ({ ...prev, isLoading: false }));
        }
      }
    });
  };

  const openCompleteModal = (sprint, e) => {
    e.stopPropagation();
    // Compute incomplete task count from current tasks in Redux store
    // rather than relying on sprint.incomplete_task_count which may be stale
    const terminalStatusKeys = new Set(
      workflowStatuses.filter(ws => ws.is_terminal || ws.category === 'done').map(ws => ws.key)
    );
    const currentSprintTasks = tasks.filter(t => t.sprint_id === sprint.id && t.task_type !== 'epic');
    const incompleteTaskCount = currentSprintTasks.filter(t => !terminalStatusKeys.has(t.status)).length;
    const completedTaskCount = currentSprintTasks.length - incompleteTaskCount;
    
    if (incompleteTaskCount === 0) {
      // No incomplete tasks - complete sprint immediately without modal
      // Show a simple confirmation dialog instead
      setConfirmModal({
        isOpen: true,
        title: 'Complete Sprint',
        message: `Are you sure you want to close the sprint "${sprint.name}"?\n\nAll ${completedTaskCount} completed issues will remain associated with this sprint.`,
        confirmText: 'Complete Sprint',
        variant: 'default',
        isLoading: false,
        onConfirm: async () => {
          setConfirmModal(prev => ({ ...prev, isLoading: true }));
          try {
            const result = await dispatch(completeSprint({
              projectId,
              sprintId: sprint.id,
              completeData: {
                move_incomplete_to_backlog: true,
                move_incomplete_to_sprint_id: null,
              },
            })).unwrap();
            toast.success(`Sprint "${sprint.name}" completed`);
            dispatch(fetchSprints({ projectId }));
            dispatch(fetchTasks({ projectId, pageSize: 5000 }));
            setConfirmModal(prev => ({ ...prev, isOpen: false, isLoading: false }));
          } catch (error) {
            toast.error(error || 'Failed to complete sprint');
            setConfirmModal(prev => ({ ...prev, isLoading: false }));
          }
        }
      });
    } else {
      // There are incomplete tasks - open modal to decide what to do with them
      setEditingSprint(sprint);
      setCompleteData({
        move_incomplete_to_backlog: true,
        move_incomplete_to_sprint_id: '',
      });
      setShowCompleteModal(true);
    }
  };

  const handleCompleteSprintDirect = async (sprint) => {
    try {
      const result = await dispatch(completeSprint({
        projectId,
        sprintId: sprint.id,
        completeData: {
          move_incomplete_to_backlog: true,
          move_incomplete_to_sprint_id: null,
        },
      })).unwrap();
      toast.success(`Sprint "${sprint.name}" completed with ${result.completed_count || 0} tasks`);
      dispatch(fetchSprints({ projectId }));
      dispatch(fetchTasks({ projectId, pageSize: 5000 }));
    } catch (error) {
      toast.error(error || 'Failed to complete sprint');
    }
  };

  const handleCompleteSprint = async () => {
    try {
      const result = await dispatch(completeSprint({
        projectId,
        sprintId: editingSprint.id,
        completeData: {
          move_incomplete_to_backlog: completeData.move_incomplete_to_backlog,
          move_incomplete_to_sprint_id: completeData.move_incomplete_to_sprint_id
            ? parseInt(completeData.move_incomplete_to_sprint_id) : null,
        },
      })).unwrap();
      toast.success(`Sprint "${result?.name || editingSprint?.name}" completed`);
      setShowCompleteModal(false);
      setEditingSprint(null);
      dispatch(fetchSprints({ projectId }));
      dispatch(fetchTasks({ projectId, pageSize: 5000 }));
    } catch (error) {
      toast.error(error || 'Failed to complete sprint');
    }
  };

  const handleDeleteSprint = (sprint, e) => {
    e.stopPropagation();
    const isActive = sprint.status === 'active';
    const warningMessage = isActive
      ? `Are you sure you want to delete the ACTIVE sprint "${sprint.name}"? All issues will be moved to the backlog. This action cannot be undone.`
      : `Are you sure you want to delete "${sprint.name}"? All issues will be moved to the backlog.`;
    
    setConfirmModal({
      isOpen: true,
      title: 'Delete Sprint',
      message: warningMessage,
      confirmText: 'Delete Sprint',
      variant: 'danger',
      isLoading: false,
      onConfirm: async () => {
        setConfirmModal(prev => ({ ...prev, isLoading: true }));
        try {
          await dispatch(deleteSprint({ projectId, sprintId: sprint.id, force: isActive })).unwrap();
          toast.success('Sprint deleted');
          dispatch(fetchTasks({ projectId, pageSize: 5000 }));
          setConfirmModal(prev => ({ ...prev, isOpen: false, isLoading: false }));
        } catch (error) {
          const errorMessage = error?.response?.data?.detail || error || 'Failed to delete sprint';
          toast.error(errorMessage);
          setConfirmModal(prev => ({ ...prev, isLoading: false }));
        }
      }
    });
  };

  // Filter handlers
  const toggleAssignee = (userId) => {
    setSelectedAssignees(prev => {
      if (prev.includes(userId)) return prev.filter(id => id !== userId);
      return [...prev, userId];
    });
  };

  const clearFilters = () => {
    setSearchQuery('');
    setSelectedAssignees([]);
    setSelectedType('');
    setSelectedSprint('');
  };

  const hasActiveFilters = searchQuery || selectedAssignees.length > 0 || selectedType || selectedSprint;

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    return format(new Date(dateStr), 'MMM d');
  };

  const plannedSprints = sprints.filter(s => s.status === 'planned');

  // Get epic info for a task
  const getEpicParent = (task) => {
    if (task.task_type === 'epic') return null;
    if (task.parent?.task_type === 'epic') return task.parent;
    return null;
  };

  // Render issue row
  const renderIssueRow = (task, depth = 0, index = 0) => {
    const canExpand = hasExpandableChildren(task);
    const isExpanded = isItemExpanded(task.id);
    const typeConfig = getTypeByKey(task.task_type) || TICKET_TYPES.task; // eslint-disable-line
    const epicParent = getEpicParent(task);

    const rowContent = (provided = null, snapshot = null) => {
      const isDragging = snapshot?.isDragging || false;
      return (
        <>
          <div
            ref={provided?.innerRef}
            {...(provided?.draggableProps || {})}
            className={`issue-row${isDragging ? ' dragging' : ''}${task.is_blocked ? ' blocked' : ''}${depth > 0 ? ' issue-row--child' : ''}`}
              onContextMenu={(e) => handleContextMenu(e, task)}
          >
            {depth === 0 && canManage && provided ? (
              <div className="drag-handle" {...provided.dragHandleProps}>
                <svg width="10" height="16" viewBox="0 0 10 16" fill="currentColor" style={{ opacity: 0.4 }}>
                  <circle cx="3" cy="3" r="1.5"/><circle cx="7" cy="3" r="1.5"/>
                  <circle cx="3" cy="8" r="1.5"/><circle cx="7" cy="8" r="1.5"/>
                  <circle cx="3" cy="13" r="1.5"/><circle cx="7" cy="13" r="1.5"/>
                </svg>
              </div>
            ) : (
              <div className="drag-handle-spacer" />
            )}

            {/* Expand/Collapse + Indent */}
            <div className="issue-expand" style={{ paddingLeft: `${8 + depth * 16}px` }}>
              {canExpand ? (
                <button
                  className="expand-btn"
                  onClick={(e) => toggleItemExpanded(task.id, e)}
                >
                  <svg viewBox="0 0 24 24" width="16" height="16" style={{ transform: isExpanded ? 'rotate(90deg)' : 'none' }}>
                    <path fill="currentColor" d="M10 6L8.59 7.41 13.17 12l-4.58 4.59L10 18l6-6z" />
                  </svg>
                </button>
              ) : (
                <span className="expand-spacer" />
              )}
            </div>

            {/* Type Icon */}
            <div className="issue-type">
              <TicketTypeIcon type={task.task_type} size="sm" config={getTypeByKey(task.task_type)} />
            </div>

            {/* Issue Key */}
            <Link
              to={getTaskUrl(task)}
              className="issue-key"
              onClick={(e) => {
                if (e.ctrlKey || e.metaKey || e.shiftKey || e.button === 1) {
                  return;
                }
                e.preventDefault();
                if (onOpenTask) {
                  onOpenTask(task.task_key);
                } else {
                  navigate(getTaskUrl(task), {
                    state: { viewMode: 'sidepanel', returnTo: location.pathname }
                  });
                }
              }}
            >
              {task.task_key}
            </Link>

            {/* Issue Title */}
            <Link
              to={getTaskUrl(task)}
              className="issue-title"
              onClick={(e) => {
                if (e.ctrlKey || e.metaKey || e.shiftKey || e.button === 1) {
                  return;
                }
                e.preventDefault();
                if (onOpenTask) {
                  onOpenTask(task.task_key);
                } else {
                  navigate(getTaskUrl(task), {
                    state: { viewMode: 'sidepanel', returnTo: location.pathname }
                  });
                }
              }}
            >
              {task.title}
            </Link>

            {/* Epic Label — only for main types (story, task); empty placeholder for others to keep grid alignment */}
            <div className="issue-epic">
              {['story', 'task'].includes(task.task_type) && (
                epicParent ? (
                  <button
                    className="epic-pill-link"
                    style={{ '--epic-color': getTypeByKey('epic')?.color || '#6554c0' }}
                    onClick={(e) => {
                      e.stopPropagation();
                      e.preventDefault();
                      if (e.ctrlKey || e.metaKey || e.shiftKey || e.button === 1) {
                        window.open(`/projects/${projectId}/board/${epicParent.task_key}`);
                      } else if (onOpenTask) {
                        onOpenTask(epicParent.task_key);
                      } else {
                        navigate(`/projects/${projectId}/board/${epicParent.task_key}`, {
                          state: { viewMode: 'modal', returnTo: location.pathname }
                        });
                      }
                    }}
                    title={`Go to Epic: ${epicParent.title}`}
                  >
                    {epicParent.title}
                  </button>
                ) : (
                  <button
                    className="add-epic-pill-btn"
                    onClick={(e) => {
                      e.stopPropagation();
                      e.preventDefault();
                      if (onOpenTask) {
                        onOpenTask(task.task_key);
                      } else {
                        navigate(getTaskUrl(task), {
                          state: { viewMode: 'sidepanel', returnTo: location.pathname }
                        });
                      }
                    }}
                    title="Add an Epic to this task"
                  >
                    + Epic
                  </button>
                )
              )}
            </div>

            {/* Parent Sprint Context - Shows when child is in backlog but parent is in sprint */}
            <div className="issue-parent-context">
              {task.parentContext?.sprintInfo && (
                <Link
                  to={`/projects/${projectId}/board/${task.parentContext.task_key}`}
                  className="parent-sprint-indicator"
                  title={`Parent "${task.parentContext.title}" is in ${task.parentContext.sprintInfo.name}`}
                >
                  <TicketTypeIcon type={task.parentContext.task_type} size="xs" config={getTypeByKey(task.parentContext.task_type)} />
                  <span className="parent-key">{task.parentContext.task_key}</span>
                  <span className="parent-sprint-badge">
                    <svg viewBox="0 0 24 24" width="10" height="10">
                      <path fill="currentColor" d="M13 7h-2v4H7v2h4v4h2v-4h4v-2h-4V7zm-1-5C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 18c-4.41 0-8-3.59-8-8s3.59-8 8-8 8 3.59 8 8-3.59 8-8 8z" />
                    </svg>
                    {task.parentContext.sprintInfo.name}
                  </span>
                </Link>
              )}
            </div>

            {/* Status - Inline Editable */}
            <div className="issue-status" onClick={(e) => e.stopPropagation()}>
              {(() => {
                const ws = workflowStatuses.find(s => s.key === task.status);
                const label = ws?.name || task.status?.replace(/_/g, ' ') || '—';
                const fallbackColors = { backlog: '#6b7280', to_do: '#6b7280', in_progress: '#3b82f6', review: '#f59e0b', done: '#10b981' };
                const color = ws?.color || fallbackColors[task.status] || '#6b7280';
                if (canManage) {
                  return (
                    <div
                      className="bl-status-editable"
                      style={{ background: color + '22', color, borderColor: color + '55' }}
                    >
                      <span className="bl-status-text">{label}</span>
                      <select
                        className="bl-status-select-overlay"
                        value={task.status}
                        onChange={(e) => saveInlineEdit(task.id, 'status', e.target.value)}
                        disabled={inlineEditSaving}
                      >
                        {workflowStatuses.filter(s => s.key !== 'backlog').map(s => (
                          <option key={s.key} value={s.key}>{s.name}</option>
                        ))}
                      </select>
                    </div>
                  );
                }
                return (
                  <span className="bl-status-badge" style={{ background: color + '22', color, borderColor: color + '55' }}>
                    {label}
                  </span>
                );
              })()}
            </div>

            {/* Children Count */}
            <div className="issue-children">
              {task.children && task.children.length > 0 && (
                <span className="children-count" title={`${task.children.length} child issues`}>
                  <svg viewBox="0 0 24 24" width="12" height="12">
                    <path fill="currentColor" d="M4 14h4v-4H4v4zm0 5h4v-4H4v4zM4 9h4V5H4v4zm5 5h12v-4H9v4zm0 5h12v-4H9v4zM9 5v4h12V5H9z" />
                  </svg>
                  {task.children.length}
                </span>
              )}
            </div>

            {/* Story Points & Actual Hours - Inline Editable */}
            <div className="issue-points" onClick={(e) => e.stopPropagation()}>
              {canManage && inlineEdit.taskId === task.id && inlineEdit.field === 'story_points' ? (
                <input
                  type="number"
                  className="inline-input inline-points"
                  value={inlineEditValue}
                  onChange={(e) => setInlineEditValue(e.target.value)}
                  onBlur={() => saveInlineEdit(task.id, 'story_points', inlineEditValue)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') saveInlineEdit(task.id, 'story_points', inlineEditValue);
                    if (e.key === 'Escape') cancelInlineEdit();
                  }}
                  min="0"
                  max="100"
                  autoFocus
                  disabled={inlineEditSaving}
                />
              ) : (
                <span
                  className={`story-points ${canManage ? 'editable' : ''}`}
                  title={canManage ? 'Click to edit story points' : 'Story points'}
                  onClick={() => canManage && startInlineEdit(task.id, 'story_points', task.story_points)}
                >
                  {task.story_points || '-'}
                </span>
              )}
              {task.actual_hours > 0 && (
                <span className="actual-hours" title="Actual hours logged">{task.actual_hours.toFixed(1)}h</span>
              )}
            </div>

            {/* Priority - Inline Editable */}
            <div className="issue-priority" onClick={(e) => e.stopPropagation()}>
              {canManage ? (
                <div className="bl-priority-editable">
                  <span className="bl-priority-dot" style={{ background: PRIORITIES[task.priority]?.color }} />
                  <select
                    className="inline-select priority-select"
                    value={task.priority}
                    onChange={(e) => saveInlineEdit(task.id, 'priority', e.target.value)}
                    disabled={inlineEditSaving}
                    title={PRIORITIES[task.priority]?.label}
                  >
                    {Object.entries(PRIORITIES).map(([key, config]) => (
                      <option key={key} value={key}>{config.label}</option>
                    ))}
                  </select>
                </div>
              ) : (
                <div className="bl-priority" title={PRIORITIES[task.priority]?.label}>
                  <span className="bl-priority-dot" style={{ background: PRIORITIES[task.priority]?.color }} />
                  <span className="bl-priority-label">{PRIORITIES[task.priority]?.label || task.priority}</span>
                </div>
              )}
            </div>

            {/* Assignee - Inline Editable */}
            <div className="issue-assignee" onClick={(e) => e.stopPropagation()}>
              {canManage ? (
                <SearchableSelect
                  options={members.map(m => ({
                    value: m.user.id,
                    label: `${m.user.first_name} ${m.user.last_name}`,
                    initials: `${m.user.first_name.charAt(0)}${m.user.last_name.charAt(0)}`
                  }))}
                  value={task.assignee?.id || null}
                  onChange={(assigneeId) => saveInlineEdit(task.id, 'assignee_id', assigneeId)}
                  placeholder="Unassigned"
                  searchPlaceholder="Search..."
                  size="sm"
                  variant="inline"
                  clearable={true}
                  disabled={inlineEditSaving}
                  preferBelow={true}
                />
              ) : task.assignee ? (
                <div className="bl-assignee">
                  <Avatar user={task.assignee} size="sm" />
                  <span className="bl-assignee-name">{task.assignee.first_name} {task.assignee.last_name}</span>
                </div>
              ) : (
                <div className="avatar avatar-sm unassigned" title="Unassigned">
                  <svg viewBox="0 0 24 24" width="14" height="14">
                    <path fill="currentColor" d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z" />
                  </svg>
                </div>
              )}
            </div>

            {/* Row Actions */}
            <div className="issue-actions" onClick={(e) => e.stopPropagation()}>
              <button
                className={`action-btn ${activeMenuTaskId === task.id ? 'active' : ''}`}
                title="More actions"
                onClick={(e) => toggleMoreActionsMenu(e, task.id)}
              >
                <svg viewBox="0 0 24 24" width="16" height="16">
                  <path fill="currentColor" d="M12 8c1.1 0 2-.9 2-2s-.9-2-2-2-2 .9-2 2 .9 2 2 2zm0 2c-1.1 0-2 .9-2 2s.9 2 2 2 2-.9 2-2-.9-2-2-2zm0 6c-1.1 0-2 .9-2 2s.9 2 2 2 2-.9 2-2-.9-2-2-2z" />
                </svg>
              </button>

              {/* More Actions Dropdown Menu */}
              {activeMenuTaskId === task.id && (
                <div
                  className="more-actions-menu"
                  ref={moreActionsRef}
                  style={{ top: menuPosition.top, left: menuPosition.left }}
                >
                  {/* Move Section */}
                  <div className="menu-section">
                    {task.sprint_id ? (
                      <button className="menu-item" onClick={() => handleMoveToSprint(task, null)}>
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <path d="M3 9h18M3 15h18M3 3h18M3 21h18"/>
                        </svg>
                        Move to Backlog
                      </button>
                    ) : (
                      <>
                        <button className="menu-item" onClick={() => handleMoveToTop(task)}>
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <polyline points="17 11 12 6 7 11" />
                            <polyline points="17 18 12 13 7 18" />
                          </svg>
                          To top of backlog
                        </button>
                        <button className="menu-item" onClick={() => handleMoveToBottom(task)}>
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <polyline points="7 13 12 18 17 13" />
                            <polyline points="7 6 12 11 17 6" />
                          </svg>
                          To bottom of backlog
                        </button>
                      </>
                    )}
                    <div
                      className={`menu-item has-submenu ${activeSubmenu === 'move' ? 'submenu-open' : ''}`}
                      onMouseEnter={() => setActiveSubmenu('move')}
                    >
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M5 9l7 7 7-7" />
                      </svg>
                      Move to sprint
                      <svg className="submenu-arrow" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <polyline points="9 18 15 12 9 6" />
                      </svg>
                      {activeSubmenu === 'move' && (
                        <div className="submenu">
                          {sprints.filter(s => s.status !== 'completed').map(sprint => (
                            <button
                              key={sprint.id}
                              className="menu-item"
                              onClick={() => handleMoveToSprint(task, sprint.id)}
                            >
                              {sprint.status === 'active' && <span className="active-indicator">★</span>}
                              {sprint.name}
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="menu-divider" />

                  {/* Copy Section */}
                  <div className="menu-section">
                    <button className="menu-item" onClick={() => handleCopyLink(task)}>
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71" />
                        <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71" />
                      </svg>
                      Copy link
                    </button>
                    <button className="menu-item" onClick={() => handleCopyKey(task)}>
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <rect x="9" y="9" width="13" height="13" rx="2" ry="2" />
                        <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
                      </svg>
                      Copy key
                    </button>
                    <button className="menu-item" onClick={() => handleCloneTask(task)}>
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <rect x="9" y="9" width="13" height="13" rx="2" ry="2" />
                        <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
                      </svg>
                      Clone
                    </button>
                  </div>

                  <div className="menu-divider" />

                  {/* Flag Section */}
                  <div className="menu-section">
                    <button className="menu-item" onClick={() => handleToggleFlag(task)}>
                      <svg width="16" height="16" viewBox="0 0 24 24" fill={task.is_flagged ? 'currentColor' : 'none'} stroke="currentColor" strokeWidth="2">
                        <path d="M4 15s1-1 4-1 5 2 8 2 4-1 4-1V3s-1 1-4 1-5-2-8-2-4 1-4 1z" />
                        <line x1="4" y1="22" x2="4" y2="15" />
                      </svg>
                      {task.is_flagged ? 'Remove flag' : 'Add flag'}
                    </button>
                  </div>

                  <div className="menu-divider" />

                  {/* Quick Edit Section */}
                  <div className="menu-section">
                    <div
                      className={`menu-item has-submenu ${activeSubmenu === 'assignee' ? 'submenu-open' : ''}`}
                      onMouseEnter={() => setActiveSubmenu('assignee')}
                    >
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
                        <circle cx="12" cy="7" r="4" />
                      </svg>
                      Assignee
                      <svg className="submenu-arrow" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <polyline points="9 18 15 12 9 6" />
                      </svg>
                      {activeSubmenu === 'assignee' && (
                        <div
                          className="submenu submenu-scrollable"
                          onWheel={(e) => e.stopPropagation()}
                        >
                          <button
                            className={`menu-item ${!task.assignee ? 'selected' : ''}`}
                            onClick={() => handleQuickAssign(task, null)}
                          >
                            Unassigned
                          </button>
                          {members.map(member => (
                            <button
                              key={member.user.id}
                              className={`menu-item ${task.assignee?.id === member.user.id ? 'selected' : ''}`}
                              onClick={() => handleQuickAssign(task, member.user.id)}
                            >
                              <span className="member-avatar">
                                {member.user.first_name.charAt(0)}{member.user.last_name.charAt(0)}
                              </span>
                              {member.user.first_name} {member.user.last_name}
                            </button>
                          ))}
                        </div>
                      )}
                    </div>

                    <div
                      className={`menu-item has-submenu ${activeSubmenu === 'priority' ? 'submenu-open' : ''}`}
                      onMouseEnter={() => setActiveSubmenu('priority')}
                    >
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <line x1="12" y1="2" x2="12" y2="22" />
                        <polyline points="19 9 12 2 5 9" />
                      </svg>
                      Priority
                      <svg className="submenu-arrow" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <polyline points="9 18 15 12 9 6" />
                      </svg>
                      {activeSubmenu === 'priority' && (
                        <div className="submenu">
                          {Object.entries(PRIORITIES).map(([key, config]) => (
                            <button
                              key={key}
                              className={`menu-item ${task.priority === key ? 'selected' : ''}`}
                              onClick={() => handleQuickPriority(task, key)}
                            >
                              <span className="priority-dot" style={{ backgroundColor: config.color }} />
                              {config.label}
                            </button>
                          ))}
                        </div>
                      )}
                    </div>

                    <div
                      className={`menu-item has-submenu ${activeSubmenu === 'parent' ? 'submenu-open' : ''}`}
                      onMouseEnter={() => setActiveSubmenu('parent')}
                    >
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <rect x="3" y="3" width="18" height="18" rx="2" />
                        <path d="M3 9h18" />
                        <path d="M9 21V9" />
                      </svg>
                      Parent
                      <svg className="submenu-arrow" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <polyline points="9 18 15 12 9 6" />
                      </svg>
                      {activeSubmenu === 'parent' && (
                        <div className="submenu submenu-scrollable">
                          {task.task_type !== 'subtask' && (
                            <button
                              className={`menu-item ${!task.parent_id ? 'selected' : ''}`}
                              onClick={() => handleQuickParent(task, null)}
                            >
                              None
                            </button>
                          )}
                          {getPotentialParents(task).map(parent => (
                            <button
                              key={parent.id}
                              className={`menu-item ${task.parent_id === parent.id ? 'selected' : ''}`}
                              onClick={() => handleQuickParent(task, parent.id)}
                            >
                              <TicketTypeIcon type={parent.task_type} size="xs" config={getTypeByKey(parent.task_type)} />
                              <span className="parent-key">{parent.task_key}</span>
                              <span className="parent-title">{parent.title}</span>
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="menu-divider" />

                  {/* Delete Section */}
                  <div className="menu-section">
                    <button
                      className="menu-item"
                      onClick={() => {
                        if (onOpenTask) {
                          onOpenTask(task.task_key);
                        } else {
                          navigate(`/tasks/${task.task_key}`, {
                            state: { viewMode: 'sidepanel', returnTo: location.pathname }
                          });
                        }
                        setActiveMenuTaskId(null);
                      }}
                    >
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                        <circle cx="12" cy="12" r="3" />
                      </svg>
                      Open details
                    </button>
                    {canManage && (
                      <button
                        className="menu-item text-danger"
                        onClick={() => handleDeleteTask(task)}
                      >
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <polyline points="3 6 5 6 21 6" />
                          <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
                        </svg>
                        Delete
                      </button>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>
          {canExpand && isExpanded && !isDragging && (
            task.children.map((child, idx) => renderIssueRow(child, depth + 1, idx))
          )}
        </>
      );
    };

    if (depth > 0) {
      return (
        <React.Fragment key={task.id}>
          {rowContent()}
        </React.Fragment>
      );
    }

    return (
      <React.Fragment key={task.id}>
        <Draggable draggableId={`task-${task.id}`} index={index} isDragDisabled={!canManage}>
          {(provided, snapshot) => rowContent(provided, snapshot)}
        </Draggable>
      </React.Fragment>
    );
  };

  if (tasksLoading && tasks.length === 0) {
    const SPRINT_ROWS = [5, 4, 6];
    const ROW_WIDTHS  = [82, 65, 90, 58, 75, 88, 50, 70];
    return (
      <div className="backlog-skeleton-wrap">
        {/* Filter bar */}
        <div className="backlog-skeleton-toolbar">
          {[100, 80, 110, 90].map((w, i) => (
            <div key={i} className="sk sk-line" style={{ width: w }} />
          ))}
        </div>
        {SPRINT_ROWS.map((rowCount, si) => (
          <div key={si} className="backlog-skeleton-sprint">
            {/* Sprint header */}
            <div className="backlog-skeleton-sprint-header">
              <div className="sk sk-circle" style={{ width: 14, height: 14 }} />
              <div className="sk sk-line-lg" style={{ width: 160 }} />
              <div className="sk sk-badge" style={{ width: 36 }} />
              <div className="sk sk-line-sm" style={{ width: 80, marginLeft: 'auto' }} />
            </div>
            {/* Task rows */}
            {Array.from({ length: rowCount }).map((_, ri) => (
              <div key={ri} className="backlog-skeleton-row">
                <div className="sk" style={{ width: 14, height: 14, borderRadius: 3 }} />
                <div className="sk sk-circle" style={{ width: 18, height: 18 }} />
                <div className="sk sk-line" style={{ width: `${ROW_WIDTHS[(si * 3 + ri) % ROW_WIDTHS.length]}%`, flex: 1 }} />
                <div className="sk sk-badge" style={{ width: 54 }} />
                <div className="sk sk-circle" style={{ width: 22, height: 22 }} />
              </div>
            ))}
          </div>
        ))}
      </div>
    );
  }

  return (
    <div className="jira-backlog">
      {/* Filter Bar */}
      <div className="backlog-toolbar">
        <div className="toolbar-left">
          {/* Search */}
          <div className="search-box">
            <svg className="search-icon" viewBox="0 0 24 24" width="16" height="16">
              <path fill="currentColor" d="M15.5 14h-.79l-.28-.27a6.5 6.5 0 0 0 1.48-5.34c-.47-2.78-2.79-5-5.59-5.34a6.505 6.505 0 0 0-7.27 7.27c.34 2.8 2.56 5.12 5.34 5.59a6.5 6.5 0 0 0 5.34-1.48l.27.28v.79l4.25 4.25c.41.41 1.08.41 1.49 0 .41-.41.41-1.08 0-1.49L15.5 14zm-6 0C7.01 14 5 11.99 5 9.5S7.01 5 9.5 5 14 7.01 14 9.5 11.99 14 9.5 14z" />
            </svg>
            <input
              type="text"
              placeholder="Search issues..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="search-input"
            />
            {searchQuery && (
              <button className="search-clear" onClick={() => setSearchQuery('')}>×</button>
            )}
          </div>

          {/* Assignee Filter */}
          <div className="assignee-filter">
            <button className="assignee-button" onClick={() => setShowAssigneeDropdown(!showAssigneeDropdown)}>
              <span className="assignee-label">Assignee</span>
              {selectedAssignees.length > 0 && (
                <span className="assignee-badge">{selectedAssignees.length}</span>
              )}
              <div className="assignee-avatars-inline">
                {selectedAssignees.slice(0, 4).map(userId => {
                  const member = members.find(m => m.user.id === userId);
                  if (!member) return null;
                  return (
                    <div key={userId} className="avatar avatar-filter avatar-sm" title={`${member.user.first_name} ${member.user.last_name}`}>
                      {member.user.first_name.charAt(0)}{member.user.last_name.charAt(0)}
                    </div>
                  );
                })}
                {selectedAssignees.length > 4 && (
                  <div className="avatar avatar-more avatar-sm">+{selectedAssignees.length - 4}</div>
                )}
              </div>
            </button>
            {showAssigneeDropdown && (
              <div className="assignee-dropdown">
                <div className="dropdown-header">Assignees</div>
                <div className="assignee-list">
                  <label className="assignee-option">
                    <input
                      type="checkbox"
                      checked={selectedAssignees.includes('unassigned')}
                      onChange={() => toggleAssignee('unassigned')}
                    />
                    <div className="avatar avatar-sm" style={{ background: '#f5f5f5', border: '1px dashed #ccc' }}>
                      ◯
                    </div>
                    <span>Unassigned</span>
                  </label>
                  {members.map(member => (
                    <label key={member.user.id} className="assignee-option">
                      <input
                        type="checkbox"
                        checked={selectedAssignees.includes(member.user.id)}
                        onChange={() => toggleAssignee(member.user.id)}
                      />
                      <div className="avatar avatar-sm">
                        {member.user.first_name.charAt(0)}{member.user.last_name.charAt(0)}
                      </div>
                      <span>{member.user.first_name} {member.user.last_name}</span>
                    </label>
                  ))}
                </div>
                {selectedAssignees.length > 0 && (
                  <button className="clear-selection" onClick={() => setSelectedAssignees([])}>
                    Clear selection
                  </button>
                )}
              </div>
            )}
          </div>

          {/* Sprint Filter — only active/planned sprints; completed are in "Completed Sprints" tab */}
          <select
            className="quick-filter-dropdown"
            value={selectedSprint}
            onChange={(e) => setSelectedSprint(e.target.value)}
            title="Filter by sprint"
          >
            <option value="">All</option>
            <option value="backlog">Backlog only</option>
            {sprints
              .filter(s => s.status === 'active' || s.status === 'planned')
              .map(sprint => (
                <option key={sprint.id} value={sprint.id}>
                  {sprint.name}{sprint.status === 'active' ? ' (active)' : ''}
                </option>
              ))
            }
          </select>

          {/* Epic Panel Toggle */}
          <button
            className={`epic-toggle-btn${showEpicPanel ? ' active' : ''}`}
            onClick={() => setShowEpicPanel(!showEpicPanel)}
            disabled={epics.length === 0}
            title={epics.length === 0 ? 'No epics in this project' : (showEpicPanel ? 'Hide epic panel' : 'Show epic panel')}
          >
            <svg width="14" height="14" viewBox="0 0 16 16" fill="currentColor">
              <path d="M1.5 1.75V13.5h13.05a.75.75 0 010 1.5H.75a.75.75 0 01-.75-.75V1.75a.75.75 0 011.5 0zm14.28 2.53l-5.25 5.25a.75.75 0 01-1.06 0L7 7.06 4.28 9.78a.75.75 0 01-1.06-1.06l3.25-3.25a.75.75 0 011.06 0L10 8.06l4.72-4.72a.75.75 0 111.06 1.06z"/>
            </svg>
            Epics
          </button>


          {/* Type Filter */}
          <select
            className="quick-filter-dropdown"
            value={selectedType}
            onChange={(e) => setSelectedType(e.target.value)}
            title="Filter by type"
          >
            <option value="">Type</option>
            {Object.entries(taskTypes).map(([key, config]) => (
              <option key={key} value={key}>{config.label}</option>
            ))}
          </select>

          {/* Clear Filters */}
          {hasActiveFilters && (
            <button className="clear-filters" onClick={clearFilters} title="Clear all filters">
              <svg viewBox="0 0 24 24" width="14" height="14">
                <path fill="currentColor" d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z" />
              </svg>
              Clear
            </button>
          )}
        </div>

        <div className="toolbar-right">
          {canManage && (
            <button className="btn btn-sm btn-primary" onClick={openCreateSprintModal}>
              Create sprint
            </button>
          )}
        </div>
      </div>

      {/* Backlog Content with Epic Panel Layout */}
      <div className={`backlog-layout ${showEpicPanel ? 'panel-open' : ''}`}>
        {/* Epic Panel */}
        <EpicPanel
          projectId={projectId}
          isOpen={showEpicPanel}
          onClose={() => setShowEpicPanel(false)}
          onEpicClick={handleEpicClick}
        />

        {/* Main Backlog Content */}
        <div className="backlog-content">
          <DragDropContext onDragEnd={handleDragEnd}>
          {/* SPRINT SECTIONS - Always First (active + planned sprints; completed hidden) */}
          {filteredData.sprints.filter(s => s.status === 'active' || s.status === 'planned').map(sprint => (
            <div key={sprint.id} className={`section sprint-section ${sprint.status}`}>
              {/* Sprint Header */}
              <div
                className={`section-header ${expandedSprints[sprint.id] ? 'expanded' : ''}`}
                onClick={() => toggleSprintExpanded(sprint.id)}
              >
                <div className="header-left">
                  <button className="expand-section-btn">
                    <svg viewBox="0 0 24 24" width="20" height="20" style={{ transform: expandedSprints[sprint.id] ? 'rotate(90deg)' : 'none' }}>
                      <path fill="currentColor" d="M10 6L8.59 7.41 13.17 12l-4.58 4.59L10 18l6-6z" />
                    </svg>
                  </button>
                  <span className="section-name">{sprint.name}</span>
                  <span className={`sprint-status-badge ${sprint.status}`}>{sprint.status}</span>
                  {sprint.start_date && sprint.end_date && (
                    <span className="sprint-dates">{formatDate(sprint.start_date)} – {formatDate(sprint.end_date)}</span>
                  )}
                  <span className="issue-count">{sprint.taskCount} issues</span>
                  {sprint.backlogChildrenCount > 0 && (
                    <span className="backlog-children-count" title={`${sprint.backlogChildrenCount} child issues in backlog`}>
                      <svg viewBox="0 0 24 24" width="12" height="12">
                        <path fill="currentColor" d="M4 14h4v-4H4v4zm0 5h4v-4H4v4zM4 9h4V5H4v4zm5 5h12v-4H9v4zm0 5h12v-4H9v4zM9 5v4h12V5H9z" />
                      </svg>
                      +{sprint.backlogChildrenCount} in backlog
                    </span>
                  )}
                </div>
                <div className="header-right">
                  {canManage && (
                    <div className="header-actions">
                      {sprint.status !== 'completed' && (
                        <button
                          className="btn btn-xs btn-ghost"
                          onClick={(e) => { e.stopPropagation(); setTargetSprintForCreate(sprint.id); setShowCreateModal(true); }}
                        >
                          + Create
                        </button>
                      )}
                      {sprint.status === 'planned' && (
                        <>
                          <button className="btn btn-xs btn-success" onClick={(e) => handleStartSprint(sprint, e)}>
                            Start sprint
                          </button>
                          <button className="btn btn-xs btn-ghost" onClick={(e) => openEditSprintModal(sprint, e)}>
                            Edit
                          </button>
                          <button className="btn btn-xs btn-ghost text-danger" onClick={(e) => handleDeleteSprint(sprint, e)}>
                            Delete
                          </button>
                        </>
                      )}
                      {sprint.status === 'active' && (
                        <>
                          <button className="btn btn-xs btn-warning" onClick={(e) => openCompleteModal(sprint, e)}>
                            Complete sprint
                          </button>
                          <button className="btn btn-xs btn-ghost" onClick={(e) => openEditSprintModal(sprint, e)}>
                            Edit
                          </button>
                          <button className="btn btn-xs btn-ghost text-danger" onClick={(e) => handleDeleteSprint(sprint, e)}>
                            Delete
                          </button>
                        </>
                      )}
                    </div>
                  )}
                </div>
              </div>
              {/* Sprint Content */}
              {expandedSprints[sprint.id] && (
                <Droppable droppableId={`sprint-${sprint.id}`}>
                  {(provided, snapshot) => (
                    <div
                      ref={provided.innerRef}
                      {...provided.droppableProps}
                      className={`section-content ${snapshot.isDraggingOver ? 'drag-over' : ''}`}
                    >
                      {sprint.tasks.length === 0 ? (
                        <div className="empty-section">
                          <p>Plan your sprint by dragging issues from the backlog</p>
                        </div>
                      ) : (
                        sprint.tasks.map((task, idx) => renderIssueRow(task, 0, idx))
                      )}
                      {provided.placeholder}
                    </div>
                  )}
                </Droppable>
              )}
            </div>
          ))}


          {/* BACKLOG SECTION - Always Last */}
          <div className="section backlog-section-container">
            {/* Backlog Header */}
            <div
              className={`section-header ${backlogExpanded ? 'expanded' : ''}`}
              onClick={() => setBacklogExpanded(!backlogExpanded)}
            >
              <div className="header-left">
                <button className="expand-section-btn">
                  <svg viewBox="0 0 24 24" width="20" height="20" style={{ transform: backlogExpanded ? 'rotate(90deg)' : 'none' }}>
                    <path fill="currentColor" d="M10 6L8.59 7.41 13.17 12l-4.58 4.59L10 18l6-6z" />
                  </svg>
                </button>
                <span className="section-name">Backlog</span>
                <span className="issue-count">{filteredData.backlogCount} issues</span>
              </div>
              <div className="header-right">
                {canManage && (
                  <button
                    className="btn btn-xs btn-ghost"
                    onClick={(e) => { e.stopPropagation(); setTargetSprintForCreate(null); setShowCreateModal(true); }}
                  >
                    + Create
                  </button>
                )}
              </div>
            </div>

            {/* Backlog Content */}
            {backlogExpanded && (
              <Droppable droppableId="backlog">
                {(provided, snapshot) => (
                  <div
                    ref={provided.innerRef}
                    {...provided.droppableProps}
                    className={`section-content ${snapshot.isDraggingOver ? 'drag-over' : ''}`}
                  >
                    {filteredData.backlog.length === 0 ? (
                      <div className="empty-section">
                        <p>Your backlog is empty</p>
                      </div>
                    ) : (
                      filteredData.backlog.map((task, idx) => renderIssueRow(task, 0, idx))
                    )}
                    {provided.placeholder}

                    {/* Quick Create at Bottom */}
                    {canManage && (
                      <div className="quick-create-row" ref={quickCreateRef}>
                        {quickCreateTarget === 'backlog' ? (
                          <form onSubmit={handleQuickCreate} className="quick-create-form">
                            <InlineTypeSelector
                              value={quickCreateType}
                              onChange={setQuickCreateType}
                              projectId={projectId}
                              excludeSubtasks
                            />
                            <input
                              type="text"
                              className="quick-create-input"
                              placeholder="What needs to be done?"
                              value={quickCreateTitle}
                              onChange={(e) => setQuickCreateTitle(e.target.value)}
                              autoFocus
                              disabled={creating}
                            />
                            <button type="submit" className="btn btn-sm btn-primary" disabled={creating || !quickCreateTitle.trim()}>
                              Create
                            </button>
                            <button type="button" className="btn btn-sm btn-ghost" onClick={() => { setQuickCreateTarget(null); setQuickCreateTitle(''); setQuickCreateType('story'); }}>
                              Cancel
                            </button>
                          </form>
                        ) : (
                          <button className="quick-create-btn" onClick={() => setQuickCreateTarget('backlog')}>
                            <svg viewBox="0 0 24 24" width="16" height="16">
                              <path fill="currentColor" d="M19 13h-6v6h-2v-6H5v-2h6V5h2v6h6v2z" />
                            </svg>
                            Create
                          </button>
                        )}
                      </div>
                    )}
                  </div>
                )}
              </Droppable>
            )}
          </div>
        </DragDropContext>
        </div>
      </div>

      {/* Create Issue Modal */}
      <Modal
        isOpen={showCreateModal}
        onClose={() => { setShowCreateModal(false); setTargetSprintForCreate(null); resetForm(); }}
        title={targetSprintForCreate ? `Create Ticket in ${filteredData.sprints.find(s => s.id === targetSprintForCreate)?.name || 'Sprint'}` : "Create New Ticket"}
        size="medium"
      >

        <form onSubmit={handleCreateTask}>
          {/* Issue Type - Jira-style selector at top */}
          <div className="form-group issue-type-selector">
            <label className="form-label">Issue Type <span className="required">*</span></label>
            <TicketTypeSelector
              value={formData.task_type}
              onChange={(type) => setFormData(prev => ({ ...prev, task_type: type, parent_id: null }))}
              required={true}
              placeholder="Select issue type"
              projectId={projectId}
            />
          </div>

          {/* Title with inline type indicator */}
          <div className="form-group">
            <label className="form-label">Title <span className="required">*</span></label>
            <div className="title-input-row">
              {formData.task_type && (
                <TicketTypeIcon type={formData.task_type} size="md" config={getTypeByKey(formData.task_type)} />
              )}
              <input
                type="text"
                className="form-input"
                value={formData.title}
                onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
                placeholder="What needs to be done?"
                autoFocus
              />
            </div>
          </div>

          <div className="form-group">
            <label className="form-label">Description</label>
            <RichTextEditor
              value={formData.description}
              onChange={(value) => setFormData(prev => ({ ...prev, description: value }))}
              placeholder="Add more details..."
              minHeight={100}
              maxHeight={200}
            />
          </div>

          <div className="form-row">
            <div className="form-group">
              <label className="form-label">Priority</label>
              <select
                className="form-select"
                value={formData.priority}
                onChange={(e) => setFormData(prev => ({ ...prev, priority: e.target.value }))}
              >
                {Object.entries(PRIORITIES).map(([key, config]) => (
                  <option key={key} value={key}>{config.label}</option>
                ))}
              </select>
            </div>

            <div className="form-group">
              <label className="form-label">Story Points</label>
              <input
                type="number"
                className="form-input"
                value={formData.story_points}
                onChange={(e) => setFormData(prev => ({ ...prev, story_points: e.target.value }))}
                placeholder="0"
                min="0"
                max="100"
              />
            </div>
          </div>

          {['task', 'story', 'subtask', 'bug'].includes(formData.task_type) && (
            <div className="form-group">
              <label className="form-label">Parent</label>
              <ParentSelector
                projectId={parseInt(projectId)}
                taskId={null}
                taskType={formData.task_type}
                currentParentId={formData.parent_id}
                onChange={(parentId) => setFormData(prev => ({ ...prev, parent_id: parentId }))}
              />
            </div>
          )}

          <div className="form-group">
            <label className="form-label">Assignee</label>
            <SearchableSelect
              options={members.map(m => ({
                value: m.user.id,
                label: `${m.user.first_name} ${m.user.last_name}`,
                email: m.user.email
              }))}
              value={formData.assignee_id}
              onChange={(assigneeId) => setFormData(prev => ({ ...prev, assignee_id: assigneeId }))}
              placeholder="Unassigned"
              searchPlaceholder="Search users..."
              getOptionDescription={(opt) => opt.email}
              clearable={true}
              preferBelow={true}
            />
          </div>

          <div className="modal-footer">
            <button type="button" className="btn btn-secondary" style={{ marginTop: '0px' }} onClick={() => { setShowCreateModal(false); resetForm(); }}>
              Cancel
            </button>
            <button type="submit" className="btn btn-primary" disabled={creating || !formData.task_type}>
              {creating ? 'Creating...' : 'Create'}
            </button>
          </div>
        </form>
      </Modal>

      {/* Sprint Modal */}
      <Modal
        isOpen={showSprintModal}
        onClose={() => setShowSprintModal(false)}
        title={editingSprint ? 'Edit Sprint' : 'Create Sprint'}
        size="small"
      >
        <form onSubmit={handleSprintSubmit}>
          <div className="form-group">
            <label className="form-label">Sprint Name <span className="required">*</span></label>
            <input
              type="text"
              className="form-input"
              value={sprintFormData.name}
              onChange={(e) => setSprintFormData({ ...sprintFormData, name: e.target.value })}
              placeholder="e.g., Sprint 1"
              autoFocus
            />
          </div>

          <div className="form-group">
            <label className="form-label">Goal</label>
            <textarea
              className="form-input"
              value={sprintFormData.goal}
              onChange={(e) => setSprintFormData({ ...sprintFormData, goal: e.target.value })}
              placeholder="What do you want to achieve?"
              rows={2}
            />
          </div>

          <div className="form-row">
            <div className="form-group">
              <label className="form-label">Start Date</label>
              <input
                type="date"
                className="form-input"
                value={sprintFormData.start_date}
                onChange={(e) => setSprintFormData({ ...sprintFormData, start_date: e.target.value })}
              />
            </div>

            <div className="form-group">
              <label className="form-label">End Date</label>
              <input
                type="date"
                className="form-input"
                value={sprintFormData.end_date}
                onChange={(e) => setSprintFormData({ ...sprintFormData, end_date: e.target.value })}
              />
            </div>
          </div>

          <div className="modal-footer">
            <button type="button" className="btn btn-secondary" style={{ marginTop: '0px' }} onClick={() => setShowSprintModal(false)}>
              Cancel
            </button>
            <button type="submit" className="btn btn-primary">
              {editingSprint ? 'Update' : 'Create'}
            </button>
          </div>
        </form>
      </Modal>

      {/* Complete Sprint Modal */}
      <Modal
        isOpen={showCompleteModal}
        onClose={() => setShowCompleteModal(false)}
        title="Complete Sprint"
        size="small"
      >
        <p className="complete-sprint-info">
          What should happen to incomplete issues in "{editingSprint?.name}"?
        </p>
        <p style={{ fontSize: '12px', color: '#666', marginBottom: '16px' }}>
          Completed issues will remain associated with this sprint.
        </p>

        <div className="form-group">
          <label className="radio-label">
            <input
              type="radio"
              checked={completeData.move_incomplete_to_backlog}
              onChange={() => setCompleteData({ move_incomplete_to_backlog: true, move_incomplete_to_sprint_id: '' })}
            />
            Move to Backlog
          </label>

          <label className="radio-label">
            <input
              type="radio"
              checked={!completeData.move_incomplete_to_backlog}
              onChange={() => setCompleteData({ move_incomplete_to_backlog: false, move_incomplete_to_sprint_id: '' })}
            />
            Move to another sprint
          </label>
        </div>

        {!completeData.move_incomplete_to_backlog && (
          <div className="form-group">
            <label className="form-label">Select Sprint</label>
            <select
              className="form-input form-select"
              value={completeData.move_incomplete_to_sprint_id}
              onChange={(e) => setCompleteData({ ...completeData, move_incomplete_to_sprint_id: e.target.value })}
            >
              <option value="">Choose a sprint...</option>
              {plannedSprints.filter(s => s.id !== editingSprint?.id).map((s) => (
                <option key={s.id} value={s.id}>{s.name}</option>
              ))}
            </select>
          </div>
        )}

        <div className="modal-footer">
          <button className="btn btn-secondary" style={{ marginTop: '0px' }} onClick={() => setShowCompleteModal(false)}>
            Cancel
          </button>
          <button
            className="btn btn-primary"
            onClick={handleCompleteSprint}
            disabled={!completeData.move_incomplete_to_backlog && !completeData.move_incomplete_to_sprint_id}
          >
            Complete Sprint
          </button>
        </div>
      </Modal>

      {cloneModalTask && (
        <CloneTaskModal
          task={cloneModalTask}
          onClose={() => setCloneModalTask(null)}
          onSuccess={(clonedTask) => {
            const label = cloneModalTask.task_type.charAt(0).toUpperCase() + cloneModalTask.task_type.slice(1);
            toast.success(`${label} cloned: ${clonedTask.task_key}`);
            dispatch(fetchTasks({ projectId, pageSize: 5000 }));
            setCloneModalTask(null);
          }}
        />
      )}

      {contextMenu.visible && contextMenu.task && (
        <div
          ref={contextMenuRef}
          className="task-ctx-menu"
          style={{ top: contextMenu.y, left: contextMenu.x }}
        >
          <button
            className="task-ctx-item"
            onClick={() => {
              window.open(`/tasks/${contextMenu.task.task_key}`, '_blank');
              setContextMenu(m => ({ ...m, visible: false }));
            }}
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M18 13v6a2 2 0 01-2 2H5a2 2 0 01-2-2V8a2 2 0 012-2h6"/>
              <polyline points="15 3 21 3 21 9"/>
              <line x1="10" y1="14" x2="21" y2="3"/>
            </svg>
            Open in new tab
          </button>
        </div>
      )}
      <ConfirmModal
        isOpen={confirmModal.isOpen}
        onClose={() => setConfirmModal(prev => ({ ...prev, isOpen: false }))}
        onConfirm={confirmModal.onConfirm}
        title={confirmModal.title}
        message={confirmModal.message}
        confirmText={confirmModal.confirmText}
        variant={confirmModal.variant}
        isLoading={confirmModal.isLoading}
      />
    </div>
  );
};

export default ProjectBacklog;

